import * as _ from 'lodash';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, ReplaySubject, Subscription } from 'rxjs';
import { catchError, map, shareReplay, take, tap, withLatestFrom } from 'rxjs/operators';

import { productImagePlaceholder, simplifyName } from 'helpers';
import { AccessState, ApiModel, Brand, ImageModel, Relations } from 'models';
import { SegmentService, UserService } from 'services';
import { TrackingEvents } from 'services/tracking/gtm';

import { BrandAccessFilters, BrandAccessRight } from '../types';
import { FileModel } from 'shared/components/modal/request-access-modal/request-access-modal.component';
import { getProductsWithTerms } from '../helpers';
import { FeaturedBrand } from 'models/brand/types';
import { PriceLevel } from 'shared/services/brands.service';

const productsToChannels = {
    [BrandAccessRight.MarketingMaterial]: 'marketing_material',
    [BrandAccessRight.ProductData]: ['products', 'ecommerce', 'internal', 'product_catalog'],
    [BrandAccessRight.EndlessAisle]: 'reorder',
    [BrandAccessRight.ReorderPlatform]: 'excalibur',
    [BrandAccessRight.PreorderPlatform]: 'preorder'
};

@Injectable({
    providedIn: 'root'
})
export class BrandAccessService {
    private brandRelationsSubject: ReplaySubject<BrandData[]> = new ReplaySubject();
    private brandRelations: Observable<BrandData[]> = this.brandRelationsSubject.asObservable().pipe(shareReplay(1));
    private featuredBrandsSubject: BehaviorSubject<FeaturedBrand[]> = new BehaviorSubject<FeaturedBrand[]>([]);
    public filteredBrands: Observable<BrandData[]>;

    private filtersSubject: BehaviorSubject<BrandAccessFilters> = new BehaviorSubject({
        name: null,
        products: [],
        categories: [],
        sustainableProducts: false,
        priceLevels: [],
        connectionStatuses: [],
        dataQualityLevels: []
    });
    private filters: Observable<BrandAccessFilters> = this.filtersSubject.asObservable().pipe(shareReplay(1));
    private _filtersInteractionContext = new BehaviorSubject<string>(null);
    public subscription: Subscription;

    constructor(
        private apiModel: ApiModel,
        private imageModel: ImageModel,
        private userService: UserService,
        private segmentService: SegmentService
    ) {
        this.filteredBrands = this.brandRelations.pipe(
            withLatestFrom(this.filters),
            tap(([brands, filters]) => {
                this.segmentService.track('Retailer brand access search', {
                    brandNameSearch: filters?.name,
                    productSearch: filters?.products,
                    segmentSearch: filters?.categories,
                    sustainabilityAttributesSearch: filters?.sustainableProducts,
                    searchResultCount: _.size(brands),
                    priceLevelSearch: filters?.priceLevels,
                    dataQualityLevelSearch: filters?.dataQualityLevels,
                    connectionStatus: filters?.connectionStatuses,
                    pageType: this._filtersInteractionContext.value
                });
            }),
            map(([brands, filters]) =>
                this.sortBrands(this.filterBrandsOnFullTrialMode(brands) as BrandData[], filters)
            ),
            shareReplay(1)
        );
    }

    static getConnectionStatus(relations: Relations): ConnectionStatus {
        const accessStates = Object.values(relations);

        const hasPending = accessStates.some((accessState) => accessState === AccessState.Pending);
        const hasGranted = accessStates.some((accessState) => accessState === AccessState.Granted);

        return hasPending
            ? ConnectionStatus.Pending
            : hasGranted
              ? ConnectionStatus.Connected
              : ConnectionStatus.Connect;
    }

    public setFiltersInteractionContext(context: 'myBrands' | 'discoverBrands' | 'brandAccess' | 'brandSearch') {
        return this._filtersInteractionContext.next(context);
    }

    public getBrands(): Observable<BrandData[]> {
        this.pullBrands();
        return this.brandRelations;
    }

    public initFilteredBrands() {
        this.subscription?.unsubscribe();
        this.subscription = this.filters.subscribe((_) => this.pullBrands());
    }

    public sendAccessRequestForBrand(
        brand: BrandData | FeaturedBrand,
        requestedProducts: BrandAccessRight[],
        note?: string,
        termsOfUseFile?: FileModel,
        connectionRequestSource?: string
    ) {
        const requestedChannels = _.flatMap(requestedProducts, (product) => productsToChannels[product]);
        const organization = this.userService.getOrganizationId();

        this.trackAccessRequest({ brand, products: requestedProducts, note, termsOfUseFile, connectionRequestSource });

        this.apiModel
            .makeHttpObservableRequest({
                method: 'POST',
                url: `${this.apiModel.getAccessEndpoint()}/request`,
                body: {
                    organization,
                    brand: brand._id,
                    requestedChannels,
                    ...(!_.isEmpty(note) ? { note } : {}),
                    ...(!_.isEmpty(termsOfUseFile) ? { termsOfUse: termsOfUseFile } : {})
                }
            })
            .pipe(
                catchError(() => of(null)),
                take(1)
            )
            .subscribe(() => {
                this.pullBrands();
                this.pullFeaturedBrands();
            });
    }

    public sendWithdrawRequestForBrand(brand: BrandData, removedProducts: BrandAccessRight[]) {
        const removedChannels = _.flatMap(removedProducts, (product) => productsToChannels[product]);
        this.trackWithdrawRequest(brand, removedProducts);

        this.apiModel
            .makeHttpObservableRequest({
                method: 'POST',
                url: `${this.apiModel.getAccessEndpoint()}/retailer/withdraw`,
                body: {
                    brandId: brand._id,
                    removedChannels
                }
            })
            .pipe(
                catchError(() => of(null)),
                take(1)
            )
            .subscribe(() => {
                this.pullBrands();
                this.pullFeaturedBrands();
            });
    }

    public setFilters(filters: Partial<BrandAccessFilters>) {
        const hasChanged = Object.keys(filters).some(
            (filterKey) => !_.isEqual(filters[filterKey], this.filtersSubject.value[filterKey])
        );
        if (hasChanged)
            this.filtersSubject.next({
                ...this.filtersSubject.getValue(),
                ...filters
            });

        if (!_.isEmpty(filters.name) && hasChanged) {
            this.segmentService.trackGtm(TrackingEvents.SEARCH, {
                page_type: 'brand_access',
                filter_brand: filters.name
            });
        }

        return { hasChanged };
    }

    public getFilters() {
        return this.filters;
    }

    public removeFromProductFilter(products: string[]) {
        this.setFilters({ products: _.without(this.filtersSubject.getValue().products, ...products) });
    }

    public removeFromCategoryFilter(categories: string[]) {
        this.setFilters({ categories: _.without(this.filtersSubject.getValue().categories, ...categories) });
    }

    public removeFromPriceLevelFilter(priceLevels: PriceLevel[]) {
        this.setFilters({ priceLevels: _.without(this.filtersSubject.getValue().priceLevels, ...priceLevels) });
    }

    public removeFromConnectionStatusFilter(connectionStatuses: string[]) {
        this.setFilters({
            connectionStatuses: _.without(this.filtersSubject.getValue().connectionStatuses, ...connectionStatuses)
        });
    }
    public removeFromDataQualityFilter(dataQualityLevels: string[]) {
        this.setFilters({
            dataQualityLevels: _.without(this.filtersSubject.getValue().dataQualityLevels, ...dataQualityLevels)
        });
    }

    public toggleFollowBrand({
        brandId,
        brandName,
        followed
    }: {
        brandId: string;
        brandName: string;
        followed: boolean;
    }) {
        return this.apiModel
            .makeHttpObservableRequest({
                method: 'POST',
                url: `${this.apiModel.getUsersEndpoint()}/toggleFollow`,
                body: {
                    brandName,
                    brandId,
                    followed
                }
            })
            .pipe(
                catchError(() => of(null)),
                take(1)
            )
            .subscribe(() => this.pullBrands());
    }

    public getFeaturedBrands(): Observable<FeaturedBrand[]> {
        if (_.isEmpty(this.featuredBrandsSubject.value)) this.pullFeaturedBrands();
        return this.featuredBrandsSubject.asObservable().pipe(shareReplay(1));
    }

    public pullFeaturedBrands() {
        this.apiModel
            .makeHttpObservableRequest<Brand[]>({
                method: 'GET',
                url: `${this.apiModel.getBrandsEndpoint()}/featured`
            })
            .pipe(
                map((brands) =>
                    this.filterBrandsOnFullTrialMode(
                        brands.map((brand) => ({
                            ..._.omit(brand, 'logos'),
                            logo: this.getBrandLogo(brand),
                            promo: this.getBrandPromo(brand),
                            connectionStatus: BrandAccessService.getConnectionStatus(brand.relations)
                        }))
                    )
                ),
                catchError(() => of([])),
                map((brands) => brands.sort((a, b) => a.order - b.order)),
                take(1)
            )
            .subscribe((brands) => {
                this.featuredBrandsSubject.next(brands);
            });
    }

    public trackBrandAccessRequest({
        brand,
        products,
        note,
        termsOfUseFile,
        connectionRequestSource
    }: {
        brand: BrandData | FeaturedBrand;
        products: BrandAccessRight[];
        note?: string;
        termsOfUseFile?: FileModel;
        connectionRequestSource?: string;
    }) {
        const path = window.location.pathname.split('/');
        const brandTerms = getProductsWithTerms(brand.termsOfUse, products);
        const featuredBrand = brand as FeaturedBrand;
        this.segmentService.track('Retailer brand access request', {
            brandName: brand.name,
            products,
            note,
            retailer_terms: !!termsOfUseFile,
            page_type: connectionRequestSource || path[path.length - 1],
            brand_terms: _.isEmpty(brandTerms) ? undefined : true,
            featured_brand: !!featuredBrand.order ? featuredBrand.order : false,
            featured_brand_source: !!featuredBrand.source ? featuredBrand.source : undefined
        });
    }

    public pullBrandById(brandId: string): Observable<BrandData> {
        return this.apiModel
            .makeHttpObservableRequest<Brand>({
                method: 'GET',
                url: `${this.apiModel.getBrandsEndpoint()}/${brandId}`
            })
            .pipe(
                map((brand) => ({
                    ..._.omit(brand, 'logos'),
                    logo: this.getBrandLogo(brand),
                    connectionStatus: BrandAccessService.getConnectionStatus(brand.relations)
                })),
                catchError(() => of(null)),
                take(1)
            );
    }

    private getBrandLogo(brand: Brand) {
        const logoFileId = _.get(brand.logos[0], 'file._id');
        return logoFileId ? this.imageModel.buildImageUrl(logoFileId, 'thumb') : productImagePlaceholder;
    }

    private getBrandPromo(brand: Brand) {
        const promoFileId = brand?.promoImage?.fileId;
        return promoFileId
            ? this.imageModel.buildImageUrl(promoFileId, 'thumb', brand.promoImage.extension)
            : undefined;
    }

    private sortBrands(brands: BrandData[], filters: BrandAccessFilters) {
        if (_.isEmpty(filters.name)) return brands;
        return brands.sort((fistBrand, secondBrand) => {
            const firstBrandNameIndex = simplifyName(fistBrand.name).indexOf(filters.name);
            const secondBrandNameIndex = simplifyName(secondBrand.name).indexOf(filters.name);
            if (firstBrandNameIndex === 0) return -1;
            if (secondBrandNameIndex === 0) return 1;
            if (firstBrandNameIndex !== -1 && secondBrandNameIndex === -1) return -1;
            if (secondBrandNameIndex !== -1 && firstBrandNameIndex === -1) return 1;
            return 0;
        });
    }

    private pullBrands(): void {
        this.apiModel
            .makeHttpObservableRequest<Brand[]>({
                method: 'GET',
                params: this.convertBrandAccessFiltersForParams(this.filtersSubject.value),
                url: `${this.apiModel.getBrandsEndpoint()}`
            })
            .pipe(
                map((brands) =>
                    brands.map((brand) => ({
                        ..._.omit(brand, 'logos'),
                        logo: this.getBrandLogo(brand),
                        connectionStatus: BrandAccessService.getConnectionStatus(brand.relations)
                    }))
                ),
                catchError(() => of([])),
                take(1)
            )
            .subscribe((brands) => this.brandRelationsSubject.next(brands));
    }

    private convertBrandAccessFiltersForParams(filters?: BrandAccessFilters) {
        return {
            ...(filters?.name ? { searchQuery: filters.name } : {}),
            ...(filters?.categories ? { categories: filters.categories } : {}),
            ...(filters?.products ? { products: filters.products } : {}),
            ...(filters?.priceLevels ? { priceLevels: filters.priceLevels } : {}),
            ...(filters?.connectionStatuses ? { connectionStatuses: filters.connectionStatuses } : {}),
            ...(filters?.dataQualityLevels ? { dataQualityLevels: filters.dataQualityLevels } : {}),
            sustainableProducts: filters.sustainableProducts
        };
    }

    private trackAccessRequest({
        brand,
        products,
        note,
        termsOfUseFile,
        connectionRequestSource
    }: {
        brand: BrandData | FeaturedBrand;
        products: BrandAccessRight[];
        note?: string;
        termsOfUseFile?: FileModel;
        connectionRequestSource?: string;
    }) {
        this.trackBrandAccessRequest({ brand, products, note, termsOfUseFile, connectionRequestSource });
        this.trackBrandAccessClickConnect({ brand, products, note, connectionRequestSource });
    }

    private trackBrandAccessClickConnect({
        brand,
        products,
        note,
        connectionRequestSource
    }: {
        brand: BrandData | FeaturedBrand;
        products: BrandAccessRight[];
        note?: string;
        connectionRequestSource?: string;
    }) {
        const path = window.location.pathname.split('/');
        const featuredBrand = brand as FeaturedBrand;
        this.segmentService.trackGtm(TrackingEvents.BRAND_ACCESS_CLICK_CONNECT, {
            brand: brand.name,
            access_type: products,
            with_note: !!note,
            page_type: connectionRequestSource || path[path.length - 1],
            featured_brand: !!featuredBrand.order ? featuredBrand.order : false,
            featured_brand_source: !!featuredBrand.source ? featuredBrand.source : undefined
        });
    }

    private trackWithdrawRequest(brand: BrandData, removedProducts: BrandAccessRight[]) {
        const organization = this.userService.getOrganizationId();
        const path = window.location.pathname.split('/');
        this.segmentService.track('Retailer brand withdraw request', {
            retailerId: organization,
            brandId: brand._id,
            products: removedProducts,
            page_type: path[path.length - 1]
        });
    }

    private filterBrandsOnFullTrialMode(brands: (BrandData | FeaturedBrand)[]) {
        return brands.filter((brand) => {
            const isBrandOnFullTrialMode = _.every(
                brand.fcProducts,
                (fcProductConfig) => fcProductConfig.trialPeriod?.active
            );

            if (isBrandOnFullTrialMode) {
                return _.every(brand.fcProducts, (fcProductConfig) =>
                    fcProductConfig.trialPeriod?.allowedRetailers.includes(this.userService.getOrganizationId())
                );
            }

            return true;
        });
    }
}

export type BrandData = Omit<Brand, 'logos'> & {
    logo: string;
    connectionStatus: ConnectionStatus;
};

export enum ConnectionStatus {
    Connected = 'connected',
    Connect = 'connect',
    Pending = 'pending'
}
